// Configuration de l'API
const API_CONFIG = {
  BASE_URL: (process.env.REACT_APP_API_URL || '') + '/api',
  TIMEOUT: 30000, // Timeout par défaut
  OSINT_TIMEOUT: 120000, // 2 minutes pour les recherches OSINT
  RETRY_ATTEMPTS: 3,
};

// Types pour les réponses API
export interface ApiResponse<T> {
  data: T;
  message?: string;
  success: boolean;
}

export interface ApiError {
  message: string;
  code?: string;
  details?: any;
}

// Classe pour gérer les erreurs API
export class ApiError extends Error {
  public status: number;
  public code?: string;
  public details?: any;

  constructor(message: string, status: number, code?: string, details?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

// Service de gestion des tokens d'authentification
class AuthService {
  private static TOKEN_KEY = 'accessToken';
  private static REFRESH_TOKEN_KEY = 'refreshToken';

  static getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  static setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  static getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  static setRefreshToken(token: string): void {
    localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
  }

  static clearTokens(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
  }

  static isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  }
}

// Classe principale pour les appels API
class ApiService {
  private static instance: ApiService;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value: any) => void;
    reject: (error: any) => void;
  }> = [];

  private constructor() {}

  static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }
    return ApiService.instance;
  }

  // Méthode générique pour les appels API
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    customTimeout?: number
  ): Promise<ApiResponse<T>> {
    const url = `${API_CONFIG.BASE_URL}${endpoint}`;
    const token = AuthService.getToken();

    const defaultHeaders: HeadersInit = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    };

    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`;
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    try {
      const controller = new AbortController();
      const timeoutDuration = customTimeout || API_CONFIG.TIMEOUT;
      const timeoutId = setTimeout(() => controller.abort(), timeoutDuration);

      const response = await fetch(url, {
        ...config,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erreur réseau' }));
        
        // Vérifier si c'est une erreur MFA
        if (response.status === 401 && errorData.requiresMFA) {
          // MFA requis, nettoyer l'état d'authentification et rediriger
          AuthService.clearTokens();
          window.location.href = '/login';
          throw new ApiError('Vérification MFA requise', response.status);
        }
        
        throw new ApiError(
          errorData.error || `HTTP ${response.status}: ${response.statusText}`,
          response.status
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      
      if ((error as any).name === 'AbortError') {
        throw new ApiError('Request timeout - La recherche a pris trop de temps', 408);
      }

      throw new ApiError(
        error instanceof Error ? error.message : 'Network error',
        500
      );
    }
  }

  // Méthode avec retry automatique
  private async requestWithRetry<T>(
    endpoint: string,
    options: RequestInit = {},
    customTimeout?: number
  ): Promise<ApiResponse<T>> {
    let lastError: ApiError | null = null;

    for (let attempt = 1; attempt <= API_CONFIG.RETRY_ATTEMPTS; attempt++) {
      try {
        return await this.request<T>(endpoint, options, customTimeout);
      } catch (error) {
        lastError = error as ApiError;
        
        // Ne pas retry sur les erreurs 4xx (sauf 429)
        if ((error as any).status >= 400 && (error as any).status < 500 && (error as any).status !== 429) {
          throw error;
        }

        if (attempt === API_CONFIG.RETRY_ATTEMPTS) {
          throw lastError;
        }

        // Attendre avant de retry (backoff exponentiel)
        await new Promise(resolve => 
          setTimeout(resolve, Math.pow(2, attempt) * 1000)
        );
      }
    }

    throw lastError || new ApiError('Unknown error', 500);
  }

  // Méthodes publiques pour les différentes ressources
  async get<T>(endpoint: string, customTimeout?: number): Promise<ApiResponse<T>> {
    return this.requestWithRetry<T>(endpoint, { method: 'GET' }, customTimeout);
  }

  async post<T>(endpoint: string, data: any, customTimeout?: number): Promise<ApiResponse<T>> {
    return this.requestWithRetry<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    }, customTimeout);
  }

  async put<T>(endpoint: string, data: any, customTimeout?: number): Promise<ApiResponse<T>> {
    return this.requestWithRetry<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    }, customTimeout);
  }

  // Méthode spécifique pour les recherches OSINT avec timeout étendu
  async osintSearch<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    return this.requestWithRetry<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    }, API_CONFIG.OSINT_TIMEOUT);
  }

  async patch<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    return this.requestWithRetry<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.requestWithRetry<T>(endpoint, { method: 'DELETE' });
  }

  // Upload de fichiers
  async uploadFile<T>(
    endpoint: string, 
    file: File, 
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    const url = `${API_CONFIG.BASE_URL}${endpoint}`;
    const token = AuthService.getToken();

    const xhr = new XMLHttpRequest();

    return new Promise((resolve, reject) => {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = (event.loaded / event.total) * 100;
          onProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve(data);
          } catch {
            reject(new ApiError('Invalid JSON response', xhr.status));
          }
        } else {
          reject(new ApiError(xhr.statusText, xhr.status));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new ApiError('Network error', 0));
      });

      xhr.open('POST', url);
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      xhr.send(formData);
    });
  }
}

// Export des instances
export const apiService = ApiService.getInstance();
export const authService = AuthService;

// Hooks personnalisés pour React
export const useApi = () => {
  return {
    get: apiService.get.bind(apiService),
    post: apiService.post.bind(apiService),
    put: apiService.put.bind(apiService),
    patch: apiService.patch.bind(apiService),
    delete: apiService.delete.bind(apiService),
    uploadFile: apiService.uploadFile.bind(apiService),
  };
}; 